/**
 * Typed API client for Jarvis Console v2
 * Mirrors all endpoints from the existing server/index.js
 */

import type {
  ApprovalListResponse,
  ApprovalDetailResponse,
  ApprovalBatchResult,
  ApprovalShownFields,
  ThreadMessage,
  ThreadResponse,
  PulseData,
  DealsResponse,
  DealDetailResponse,
  DealTimelineResponse,
  DealDocumentPreviewResponse,
  PipelineStats,
  NextClosingResponse,
  ContactSearchResult,
  ContactProfile,
  CorePersonResponse,
  PersonTextMessage,
  PersonTextsResponse,
  CorePersonPromise,
  OutreachQueueResponse,
  OutreachDraftResponse,
  OutreachStatsResponse,
  ChatSession,
  ChatMessage,
  ChatMessageSender,
  MessageThreadsResponse,
  SendSmsResponse,
  PeopleResponse,
  PeopleSearchResponse,
  PeoplePersonResponse,
  PeopleDraftResponse,
  ISAApprovalListResponse,
  ISAThreadsResponse,
  ISAThreadDetailResponse,
  ISASendResponse,
  AwarenessResponse,
  TodayResponse,
  DashboardNowResponse,
  DashboardItem,
  AutopilotColdLine,
  AutopilotPerson,
  AutopilotReady,
  AutopilotSwitch,
  AutopilotLook,
  AutopilotLookState,
  AutopilotMail,
  AutopilotMailKind,
  AutopilotConversation,
  AutopilotResponse,
  AutopilotState,
  AutopilotStatus,
  CoreStatusResponse,
  ReachingResponse,
  EngineHealthResponse,
  EngineTodayResponse,
  LedgerTodayBuildDesk,
  LedgerTodayStopNotice,
  LedgerTodayLineWarning,
  LedgerTodayParked,
  LedgerTodayBuildDeskQuestion,
  LedgerTodayScorecard,
  LedgerTodayScorecardSnapshot,
  LedgerTodayGolive,
  LedgerTodayResponse,
  LedgerGroup,
  NeedLedger,
  OwnerAvailability,
  TelegramTimelineResponse,
} from './types';

export interface CodexModel {
  slug: string;
  label: string;
  description: string;
  efforts: string[];
  default_effort: string;
  supports_fast: boolean;
  priority: number;
}

export interface EngineProfile {
  chat_id: number;
  provider: 'claude' | 'codex';
  claude: {
    model: string;
    effort: string;
  };
  codex: {
    model: string;
    effort: string;
    fast: boolean;
  };
}

export interface EngineProfilePatch {
  provider?: 'claude' | 'codex';
  claude_model?: string;
  claude_effort?: string;
  codex_model?: string;
  codex_effort?: string;
  codex_fast?: boolean;
}

export interface TerminalSession {
  name: string;
  kind: 'architect' | 'daemon' | 'pwa' | 'codex' | 'claude';
  provider?: 'claude' | 'codex';
  interactive?: boolean;
  attached: boolean | number;
  created: string | number;
  chat_id?: number | string;
  title?: string | null;
  model?: string;
  effort?: string;
  agent_role?: 'main' | 'worker';
  status?: 'running' | 'completed' | 'failed' | 'stopped';
  updated?: string | number;
  thread_id?: string;
}

type RawThreadResponse = Record<string, unknown> & {
  messages?: Array<Record<string, unknown>>;
};

const JOSEPH_EMAILS = new Set(['joe@janiczekgroup.com', 'joejaniczek@gmail.com']);

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}

/** Normalize the three legacy thread payload shapes into the UI contract. */
export function normalizeThreadResponse(
  data: RawThreadResponse,
  defaultChannel?: ThreadMessage['channel'],
): ThreadResponse {
  const messages = (Array.isArray(data.messages) ? data.messages : []).map((raw, index) => {
    const channel: ThreadMessage['channel'] = raw.channel === 'email'
      || (!defaultChannel && ('gmail_id' in raw || 'from_address' in raw))
      ? 'email'
      : defaultChannel || 'sms';
    const fromAddress = optionalString(raw.from_address);
    const fromName = optionalString(raw.from_name);
    const from = optionalString(raw.from)
      || optionalString(raw.from_number)
      || (fromName && fromAddress ? `${fromName} <${fromAddress}>` : fromAddress);
    const rawDirection = String(raw.direction || '').toLowerCase();
    const isOutbound = rawDirection === 'outbound'
      || rawDirection === 'outgoing'
      || (channel === 'email' && !!fromAddress && JOSEPH_EMAILS.has(fromAddress.toLowerCase()));
    const timestamp = String(raw.timestamp ?? raw.date ?? raw.created_at ?? '');

    return {
      id: String(raw.id ?? raw.gmail_id ?? `${channel}-${index}-${timestamp}`),
      direction: isOutbound ? 'outbound' : 'inbound',
      body: String(raw.body ?? raw.snippet ?? ''),
      channel,
      timestamp,
      from,
      to: optionalString(raw.to) || optionalString(raw.to_number) || optionalString(raw.to_addrs),
      subject: optionalString(raw.subject),
    } satisfies ThreadMessage;
  });

  const contact = data.contact && typeof data.contact === 'object'
    ? data.contact as Record<string, unknown>
    : null;
  return {
    messages,
    contact_name: optionalString(data.contact_name) || optionalString(contact?.name),
  };
}

export interface BackgroundTask {
  task_id: string;
  label: string;
  state: 'scheduled' | 'running' | 'done' | 'stale' | 'orphaned' | 'unknown';
  elapsed_s: number;
  log_tail: string;
}

export interface DurableTurn {
  turn_id: string;
  chat_id: number;
  provider: 'claude' | 'codex' | string;
  model: string;
  effort: string;
  state: 'accepted' | 'running' | 'waiting_capacity' | 'completed' | 'failed' | 'interrupted' | 'cancelled';
  phase: string;
  activity: string;
  tool_calls: number;
  last_tool: string;
  started_at: string;
  heartbeat_at: string;
  activity_at: string;
  completed_at: string | null;
  result: string | null;
  error: string | null;
  history_persisted: boolean;
  acked_at: string | null;
}

export interface ChatStatusResponse {
  streaming: boolean;
  streaming_chats: Record<string, Record<string, unknown>>;
  turns: DurableTurn[];
  server_time: string;
}

export interface ChatHistoryPage {
  messages: ChatMessage[];
  has_more?: boolean;
  next_before_id?: string | null;
}

export interface ChatSearchResponse {
  results: Array<{
    chat_id: number;
    title: string;
    match_type: 'title' | 'message';
    snippet: string | null;
    matched_at: string | null;
  }>;
  has_more: boolean;
  next_offset: number | null;
}

export interface ChatSteeringReceipt {
  id: string;
  text: string;
  status: 'pending' | 'sending' | 'accepted' | 'failed' | 'unknown';
  error?: string;
}

export interface ChatSteeringResponse {
  ok: true;
  supported: boolean;
  turn_id: string | null;
  requests: ChatSteeringReceipt[];
  request?: ChatSteeringReceipt;
}

export interface PromptQuestionOption {
  label: string;
  description?: string;
}

export interface PromptQuestion {
  question: string;
  header?: string;
  multiSelect?: boolean;
  options: PromptQuestionOption[];
}

/** A question or plan a coding tab is waiting on (PWA PH-02). */
export interface ChatPrompt {
  id: string;
  chat_id: number;
  kind: 'question' | 'plan';
  tool_name: string;
  input: { questions?: PromptQuestion[]; plan?: string; [key: string]: unknown };
  created_at: number;
  status: string;
  live?: boolean;
}

export interface ChatPromptsResponse {
  ok: boolean;
  relay: boolean;
  prompts: ChatPrompt[];
}

export type ChatPromptAnswer =
  | { answers: Record<string, string> }
  | { decision: 'approve' }
  | { decision: 'keep_planning'; feedback: string };

export interface AppPreferences {
  textScale: number;
  updatedAt: string | null;
}

const FILE_READ_CHUNK_SIZE = 4 * 1024 * 1024;
const FILE_READ_ATTEMPTS = 3;
const CHUNK_UPLOAD_ATTEMPTS = 3;

export class FileReadIntegrityError extends Error {
  constructor(
    public expected: number,
    public received: number,
    public offset: number,
  ) {
    super(`Could only read ${received} of ${expected} bytes at offset ${offset}`);
    this.name = 'FileReadIntegrityError';
  }
}

async function readRangeExactly(file: File, start: number, end: number): Promise<Uint8Array> {
  const expected = end - start;
  let received = 0;
  for (let attempt = 0; attempt < FILE_READ_ATTEMPTS; attempt += 1) {
    try {
      const candidate = new Uint8Array(await file.slice(start, end).arrayBuffer());
      received = candidate.byteLength;
      if (received === expected) return candidate;
    } catch {
      received = 0;
    }
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
  }
  throw new FileReadIntegrityError(expected, received, start);
}

function tailFirstRanges(size: number, chunkSize: number): Array<{ start: number; end: number }> {
  const ranges: Array<{ start: number; end: number }> = [];
  for (let start = 0; start < size; start += chunkSize) {
    ranges.push({ start, end: Math.min(start + chunkSize, size) });
  }
  const tail = ranges.pop();
  return tail ? [tail, ...ranges] : ranges;
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes as BufferSource);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Read a File fully into memory via bounded slices. Android content providers
 * can return a short buffer without throwing. Samsung m4a files keep their
 * index at the end, so request the tail first while the picker permission is
 * freshest, retry every short range, and fail if any byte remains unread.
 */
export async function readFileFully(file: File): Promise<Uint8Array> {
  const size = file.size;
  if (size === 0) return new Uint8Array(0);

  const out = new Uint8Array(size);

  for (const { start, end } of tailFirstRanges(size, FILE_READ_CHUNK_SIZE)) {
    const chunk = await readRangeExactly(file, start, end);
    out.set(chunk, start);
  }

  return out;
}

export class ApiError extends Error {
  constructor(message: string, public status: number, public data?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** The server attaches a sender to a history row that a tab, worker or wake sent into the chat. */
function parseSender(raw: unknown): ChatMessageSender | undefined {
  if (!isRecord(raw) || raw.kind !== 'automatic' || typeof raw.label !== 'string') return undefined;
  const label = raw.label.trim();
  return label ? { kind: 'automatic', label } : undefined;
}

function hasNullableIdentifier(row: Record<string, unknown>, key: 'contact_id' | 'deal_id'): boolean {
  if (!Object.prototype.hasOwnProperty.call(row, key)) return false;
  const value = row[key];
  return value === null || (typeof value === 'string' && value.trim().length > 0);
}

function validCompletedTarget(row: unknown): boolean {
  if (!isRecord(row) || !hasNullableIdentifier(row, 'contact_id') || !hasNullableIdentifier(row, 'deal_id')) {
    return false;
  }
  return (row.contact_id === null) !== (row.deal_id === null);
}

const ENGINE_LEDGER_GROUPS: LedgerGroup[] = ['queued', 'running', 'waiting_on', 'blocked', 'verified_done'];

function isCount(value: unknown): value is number {
  return Number.isSafeInteger(value) && (value as number) >= 0;
}

function sameReasonCounts(first: Record<string, number>, second: Record<string, number>): boolean {
  const keys = Object.keys(first);
  return keys.length === Object.keys(second).length && keys.every((key) => first[key] === second[key]);
}

function normalizeNeedLedger(value: unknown): NeedLedger {
  if (!isRecord(value) || !isRecord(value.counts) || !isRecord(value.excluded_reasons)) {
    throw new TypeError('invalid need ledger reconciliation');
  }
  const counts = value.counts;
  const sourceCount = value.source_count;
  const excludedCount = value.excluded_count;
  if (!isCount(sourceCount) || !isCount(excludedCount) || !isCount(counts.total)
      || sourceCount - excludedCount !== counts.total) {
    throw new TypeError('invalid need ledger reconciliation');
  }
  let bucketTotal = 0;
  for (const group of ENGINE_LEDGER_GROUPS) {
    if (!Array.isArray(value[group]) || !isCount(counts[group])) {
      throw new TypeError('invalid need ledger reconciliation');
    }
    bucketTotal += counts[group];
    for (const row of value[group]) {
      if (!isRecord(row) || typeof row.need_id !== 'string' || !row.need_id.trim()) {
        throw new TypeError('invalid need ledger row');
      }
    }
  }
  const reasonTotal = Object.values(value.excluded_reasons).reduce<number>((total, count) => {
    if (!isCount(count)) throw new TypeError('invalid need ledger exclusion');
    return total + count;
  }, 0);
  if (bucketTotal !== counts.total || reasonTotal !== excludedCount
      || (value.next_cursor !== null && typeof value.next_cursor !== 'string')) {
    throw new TypeError('invalid need ledger reconciliation');
  }
  return value as unknown as NeedLedger;
}

// --- Today ledger view (jarvis-today PH-01): keep well-formed rows, drop the rest. ---

type Row = Record<string, unknown>;
const str = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value : null);
const num = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);
/**
 * Only same-site paths survive, so a bad row can never carry a script or off-site link. Browsers drop
 * tabs and newlines inside URLs and read a backslash as a slash (`/\t/evil.com` becomes `//evil.com`),
 * so those are refused outright and the rest must resolve to the same origin.
 */
const SITE_ORIGIN = 'https://site.invalid';
const sitePath = (value: unknown): string | null => {
  if (typeof value !== 'string' || !value.startsWith('/') || /[\u0000-\u001f\u007f\\]/.test(value)) return null;
  try {
    const url = new URL(value, SITE_ORIGIN);
    return url.origin === SITE_ORIGIN ? `${url.pathname}${url.search}${url.hash}` : null;
  } catch {
    return null;
  }
};

function rows<T>(value: unknown, pick: (row: Row) => T | null): T[] | null {
  if (!Array.isArray(value)) return null;
  const out: T[] = [];
  for (const row of value) {
    if (!isRecord(row)) continue;
    const picked = pick(row);
    if (picked !== null) out.push(picked);
  }
  return out;
}

function needRow(row: Row) {
  const needId = str(row.need_id);
  const subject = str(row.subject);
  if (!needId || !subject) return null;
  return { need_id: needId, subject, detail: str(row.detail) ?? '', href: sitePath(row.href) };
}

function buildDesk(value: unknown): LedgerTodayBuildDesk {
  const desk = isRecord(value) ? value : {};
  const id = (row: Row): number | null => (Number.isSafeInteger(row.id) && (row.id as number) > 0 ? row.id as number : null);
  return {
    questions: rows(desk.questions, (row): LedgerTodayBuildDeskQuestion | null => {
      const qid = id(row);
      const question = str(row.question);
      const track = str(row.track);
      if (qid === null || !question || !track || (row.status !== 'open' && row.status !== 'answered')) return null;
      const options = Array.isArray(row.options) ? row.options.filter((o): o is string => typeof o === 'string' && !!o.trim()) : [];
      return {
        id: qid, track, item: str(row.item), question, context: str(row.context), link: sitePath(row.link), options,
        recommendation: str(row.recommendation), blocking: row.blocking === true, status: row.status,
        answer: str(row.answer), answer_option: str(row.answer_option), answered_at: str(row.answered_at),
        created_at: str(row.created_at),
      };
    }),
    golives: rows(desk.golives, (row): LedgerTodayGolive | null => {
      const gid = id(row);
      const text = str(row.text);
      const track = str(row.track);
      const created = str(row.created_at);
      return gid !== null && text && track && created ? { id: gid, track, item: str(row.item), text, created_at: created } : null;
    }),
  };
}

function scorecardSnapshot(value: unknown, period: 'day' | 'week'): LedgerTodayScorecardSnapshot | null {
  if (!isRecord(value) || value.period !== period || typeof value.period_key !== 'string') return null;
  const rate = (v: unknown) => { const n = num(v); return n !== null && n >= 0 && n <= 1 ? n : null; };
  const count = (v: unknown) => (Number.isSafeInteger(v) && (v as number) >= 0 ? v as number : null);
  const whole = (v: unknown) => (Number.isSafeInteger(v) ? v as number : null);
  const d = isRecord(value.delta) ? value.delta : null;
  return {
    period, period_key: value.period_key, generated_at: str(value.generated_at),
    as_is_rate: rate(value.as_is_rate), decided: count(value.decided), sent_untouched: count(value.sent_untouched),
    unanswered_people: count(value.unanswered_people), telegram_sent: count(value.telegram_sent),
    delta: d && {
      previous_key: str(d.previous_key), as_is_rate: num(d.as_is_rate),
      unanswered_people: whole(d.unanswered_people), telegram_sent: whole(d.telegram_sent),
    },
    href: sitePath(value.href), stale: value.stale === true,
  };
}

function scorecard(value: unknown): LedgerTodayScorecard | null {
  if (!isRecord(value)) return null;
  return { day: scorecardSnapshot(value.day, 'day'), week: scorecardSnapshot(value.week, 'week') };
}

function normalizeCorePerson(data: unknown): CorePersonResponse {
  if (!isRecord(data)) throw new TypeError('invalid core person response');
  const time = (value: unknown) => {
    const text = str(value);
    return text && Number.isFinite(Date.parse(text)) ? text : null;
  };
  const page = isRecord(data.page) && str(data.page.body) ? data.page : null;
  const look = isRecord(data.last_look) && time(data.last_look.finished_at) ? data.last_look : null;
  return {
    contact_id: str(data.contact_id) ?? '', generated_at: time(data.generated_at),
    page: page ? { body: page.body as string, covers_through: time(page.covers_through),
      written_at: time(page.written_at), by_jarvis: page.by_jarvis === true } : null,
    last_look: look ? { finished_at: time(look.finished_at)!, summary: str(look.summary) ?? '',
      nothing_owed: look.nothing_owed === true, cards: num(look.cards) ?? 0,
      for_joseph: Array.isArray(look.for_joseph)
        ? look.for_joseph.filter((line): line is string => typeof line === 'string') : [] } : null,
    promises: rows(data.promises, (row): CorePersonPromise | null => {
      const what = str(row.what);
      const move = row.move === 'yours_tap' || row.move === 'yours' || row.move === 'theirs' ? row.move : 'jarvis';
      return what ? { label: str(row.label) ?? '', what, state: str(row.state) ?? '', move,
        party: str(row.party), next_at: time(row.next_at), first_due: time(row.first_due), late: row.late === true } : null;
    }),
    unavailable: Array.isArray(data.unavailable)
      ? data.unavailable.filter((part): part is string => typeof part === 'string') : [],
  };
}

function normalizeLedgerToday(data: unknown): LedgerTodayResponse {
  if (!isRecord(data)) throw new TypeError('invalid ledger today response');
  const needs = isRecord(data.needs_joseph) ? data.needs_joseph : {};
  const counts: LedgerTodayResponse['counts'] = {};
  if (isRecord(data.counts)) {
    for (const key of ['needs_joseph', 'done_today', 'sent_today', 'waiting', 'blocked', 'next_7_days', 'jarvis_did', 'build_desk_open', 'promises_late', 'on_your_list'] as const) {
      counts[key] = num(data.counts[key]);
    }
  }
  return {
    generated_at: str(data.generated_at) ?? new Date().toISOString(),
    day: str(data.day) ?? '',
    needs_joseph: {
      approvals: rows(needs.approvals, (row) => {
        const id = str(row.approval_id);
        return id ? { approval_id: id, title: str(row.title) ?? 'Approval', contact_name: str(row.contact_name),
          action_type: str(row.action_type), created_at: str(row.created_at), href: sitePath(row.href) } : null;
      }),
      decisions: rows(needs.decisions, (row) => {
        const base = needRow(row);
        if (!base) return null;
        const texts = (value: unknown) => (Array.isArray(value)
          ? value.filter((item): item is string => typeof item === 'string' && !!item.trim()) : []);
        const question = str(row.question);
        const version = str(row.version);
        return { ...base, due_at: str(row.due_at), question, options: question ? texts(row.options) : [],
          version: question && version && /^[0-9a-f]{64}$/.test(version) ? version : null,
          recommended: question ? str(row.recommended) : null, by_when: question ? str(row.by_when) : null,
          facts: question ? texts(row.facts) : [] };
      }),
      // F-03b: a need parked on Joseph; absent stays absent for an older server.
      ...('parked' in needs ? {
        parked: rows(needs.parked, (row): LedgerTodayParked | null => {
          const base = needRow(row);
          const until = str(row.parked_until);
          return base ? { ...base, parked_until: until && Number.isFinite(Date.parse(until)) ? until : null } : null;
        }),
      } : {}),
      flags: rows(needs.flags, (row) => {
        const text = str(row.text);
        const at = str(row.at);
        if (!text || !at || (row.severity !== 'warning' && row.severity !== 'critical')) return null;
        return { severity: row.severity, text, at, lane: str(row.lane), href: sitePath(row.href) };
      }),
    },
    on_your_list: rows(data.on_your_list, (row) => {
      const base = needRow(row);
      return base ? { ...base, due_at: str(row.due_at) } : null;
    }),
    // F-03: kept word for word (the writer already holds each to 300); absent stays absent for an older server.
    ...('stop_notices' in data ? {
      stop_notices: rows(data.stop_notices, (row): LedgerTodayStopNotice | null => {
        const at = str(row.at);
        return str(row.text) && at && Number.isFinite(Date.parse(at))
          ? { text: row.text as string, at, href: sitePath(row.href) } : null;
      }),
    } : {}),
    // F-03b: kept word for word, like the stops.
    ...('line_warnings' in data ? {
      line_warnings: rows(data.line_warnings, (row): LedgerTodayLineWarning | null => {
        const at = str(row.at);
        return str(row.text) && at && Number.isFinite(Date.parse(at))
          ? { text: row.text as string, at, line: str(row.line) ?? 'main' } : null;
      }),
    } : {}),
    stopped_jobs: rows(data.stopped_jobs, (row) => {
      const text = str(row.text);
      const at = str(row.at);
      return text && at ? { text, at } : null;
    }),
    urgent_notes: rows(data.urgent_notes, (row) => {
      const text = str(row.text);
      const at = str(row.at);
      return text && at ? { text, at, href: sitePath(row.href) } : null;
    }),
    done_today: rows(data.done_today, (row) => {
      const base = needRow(row);
      const verified = str(row.verified_at);
      return base && verified ? { ...base, verified_at: verified } : null;
    }),
    sent_today: rows(data.sent_today, (row) => {
      const id = str(row.approval_id);
      return id ? { approval_id: id, title: str(row.title) ?? 'Message', contact_name: str(row.contact_name),
        action_type: str(row.action_type), sent_at: str(row.sent_at), href: sitePath(row.href) } : null;
    }),
    waiting: rows(data.waiting, (row) => {
      const base = needRow(row);
      return base ? { ...base, party: str(row.party), since: str(row.since), follow_up_at: str(row.follow_up_at),
        days_waiting: num(row.days_waiting) } : null;
    }),
    blocked: rows(data.blocked, (row) => {
      const base = needRow(row);
      return base ? { ...base, reason: str(row.reason), until: str(row.until) } : null;
    }),
    next_7_days: rows(data.next_7_days, (row) => {
      const at = str(row.at);
      const label = str(row.label);
      const type = row.type;
      if (!at || !label || !(type === 'showing' || type === 'event' || type === 'deadline' || type === 'need_due')) return null;
      return { at, type, label, href: sitePath(row.href), all_day: row.all_day === true };
    }),
    jarvis_did: rows(data.jarvis_did, (row) => {
      const id = str(row.run_id);
      const finished = str(row.finished_at);
      if (!id || !finished) return null;
      return { run_id: id, lane: str(row.lane) ?? 'assistant', finished_at: finished, status: str(row.status) ?? 'unknown',
        handled: num(row.handled) ?? 0, left: num(row.left) ?? 0, summary: str(row.summary) ?? '', href: sitePath(row.href) };
    }),
    build_desk: buildDesk(data.build_desk),
    scorecard: scorecard(data.scorecard),
    counts,
  };
}

const CORE_STATES = new Set(['on', 'off', 'paused', 'quiet', 'capped', 'unknown']);

function normalizeCoreStatus(data: unknown): CoreStatusResponse {
  if (!isRecord(data)) throw new TypeError('invalid core status response');
  const whole = (value: unknown) => (Number.isSafeInteger(value) && (value as number) >= 0 ? value as number : null);
  const time = (value: unknown) => {
    const text = str(value);
    return text && Number.isFinite(Date.parse(text)) ? text : null;
  };
  const wakes = isRecord(data.wakes_today) ? data.wakes_today : {};
  const last = isRecord(data.last_wake) ? data.last_wake : null;
  const lastAt = last ? time(last.finished_at) : null;
  const lastName = last ? str(last.first_name) : null;
  const waker = isRecord(data.waker) ? data.waker : {};
  return {
    available: data.available === true,
    checked_at: time(data.checked_at),
    state: typeof data.state === 'string' && CORE_STATES.has(data.state) ? data.state as CoreStatusResponse['state'] : 'unknown',
    reasons: Array.isArray(data.reasons) ? data.reasons.filter((r): r is string => typeof r === 'string') : [],
    wakes_today: { started: whole(wakes.started), done: whole(wakes.done), cap: whole(wakes.cap) },
    running: whole(data.running),
    waiting_to_start: whole(data.waiting_to_start),
    failed_today: whole(data.failed_today),
    last_wake: last && lastAt && lastName ? { finished_at: lastAt, first_name: lastName, href: sitePath(last.href) } : null,
    waker: { checked_at: time(waker.checked_at), quiet: typeof waker.quiet === 'boolean' ? waker.quiet : null },
  };
}

const AUTOPILOT_STATES = new Set(['open', 'answered', 'handed_off', 'stopped', 'unknown', 'nothing_owed']);
const AUTOPILOT_STATUSES = new Set(['received', 'sent', 'delivered', 'undelivered', 'failed', 'queued', 'sending', 'cancelled']);
const LOOK_STATES = ['done', 'failed', 'running', 'ready', 'cancelled'] as const;
const MAIL_KINDS = new Set(['link_sent', 'reminder', 'link_resent', 'signed_copy']);

/** jarvis-core F-02c: a text without a valid time is dropped, an unknown status reads other, a stream that is not a list is null. */
function normalizePersonTexts(data: unknown, id: string): PersonTextsResponse {
  if (!isRecord(data)) throw new TypeError('invalid person texts response');
  const stream = (value: unknown, extra: (row: Row) => Partial<PersonTextMessage>) => rows(value, (row): PersonTextMessage | null => {
    const at = str(row.at);
    if (!at || !Number.isFinite(Date.parse(at))) return null;
    return {
      at, who: row.who === 'them' ? 'them' : 'us', text: typeof row.text === 'string' ? row.text : null,
      status: typeof row.status === 'string' && AUTOPILOT_STATUSES.has(row.status) ? row.status as AutopilotStatus : 'other',
      ...extra(row),
    };
  });
  return {
    contact_id: str(data.contact_id) ?? id,
    cold_line: stream(data.cold_line, () => ({})),
    h2o: stream(data.h2o, (row) => ({ with_card: row.with_card === true })),
    taylor: stream(data.taylor, (row) => ({ name: str(row.name) })),
  };
}

/** jarvis-core F-02a: unknown states read as open, unknown statuses as other, off-site person links are dropped. */
function normalizeAutopilot(data: unknown): AutopilotResponse {
  if (!isRecord(data)) throw new TypeError('invalid autopilot response');
  const whole = (value: unknown) => (Number.isSafeInteger(value) && (value as number) >= 0 ? value as number : null);
  const time = (value: unknown) => {
    const text = str(value);
    return text && Number.isFinite(Date.parse(text)) ? text : null;
  };
  const who = (value: unknown): 'them' | 'us' => (value === 'them' ? 'them' : 'us');
  const status = (value: unknown): AutopilotStatus =>
    (typeof value === 'string' && AUTOPILOT_STATUSES.has(value) ? value as AutopilotStatus : 'other');
  const person = (value: unknown): AutopilotPerson | null => {
    if (!isRecord(value)) return null;
    const first = str(value.first_name);
    return first ? { first_name: first, href: sitePath(value.href) } : null;
  };
  const conversation = (row: Row): AutopilotConversation | null => {
    const who_ = person(row.person);
    if (!who_) return null;
    const messages = rows(row.messages, (m) => {
      const at = time(m.at);
      return at ? { at, who: who(m.who), text: typeof m.text === 'string' ? m.text : null, status: status(m.status) } : null;
    }) ?? [];
    return {
      person: who_,
      state: typeof row.state === 'string' && AUTOPILOT_STATES.has(row.state) ? row.state as AutopilotState : 'open',
      last_at: time(row.last_at), answers: whole(row.answers), messages,
    };
  };
  const cold = isRecord(data.cold_line) ? data.cold_line : null;
  let coldLine: AutopilotColdLine | null = null;
  if (cold) {
    const sw = isRecord(cold.switches) ? cold.switches : {};
    const today = isRecord(cold.today) ? cold.today : {};
    coldLine = {
      available: cold.available === true,
      switches: {
        sending: sw.sending === 'on' || sw.sending === 'off' ? sw.sending : null,
        paused: typeof sw.paused === 'boolean' ? sw.paused : null,
        pause_reason: str(sw.pause_reason),
      },
      today: { sent: whole(today.sent), delivered: whole(today.delivered), failed: whole(today.failed), answers: whole(today.answers) },
      waiting_to_go: whole(cold.waiting_to_go),
      not_linked: whole(cold.not_linked),
      conversations: rows(cold.conversations, conversation),
      ready_for_you: rows(cold.ready_for_you, (row): AutopilotReady | null => {
        const who_ = person(row.person);
        const at = time(row.at);
        return who_ && at ? { person: who_, at, why: str(row.why), next: str(row.next) } : null;
      }),
    };
  }
  const taylor = isRecord(data.taylor) ? data.taylor : null;
  const h2o = isRecord(data.h2o) ? data.h2o : null;
  const core = isRecord(data.core) ? data.core : null;
  const coreToday = core && isRecord(core.today) ? core.today : null;
  const signing = isRecord(data.signing) ? data.signing : null;
  // F-02b keys: absent stays absent (an older server), anything else that is not an object is null (unreadable).
  const more: Pick<AutopilotResponse, 'switches' | 'h2o' | 'core' | 'signing'> = {};
  if ('switches' in data) {
    more.switches = rows(data.switches, (row): AutopilotSwitch | null => {
      const key = str(row.key);
      return key ? { key, state: row.state === 'on' || row.state === 'off' ? row.state : null, since: time(row.since), note: str(row.note) } : null;
    });
  }
  if ('h2o' in data) {
    more.h2o = h2o ? { available: h2o.available === true, conversations: rows(h2o.conversations, conversation), not_linked: whole(h2o.not_linked) } : null;
  }
  if ('core' in data) {
    more.core = core ? {
      available: core.available === true,
      today: coreToday ? Object.fromEntries(LOOK_STATES.map((s) => [s, whole(coreToday[s])])) as Record<AutopilotLookState, number | null> : null,
      looks: rows(core.looks, (row): AutopilotLook | null => {
        const at = time(row.at);
        if (!at) return null;
        const state = LOOK_STATES.find((s) => s === row.state) ?? null;
        return { person: person(row.person), state, reason: str(row.reason), at, summary: str(row.summary), why_stopped: str(row.why_stopped) };
      }),
    } : null;
  }
  if ('signing' in data) {
    more.signing = signing ? {
      available: signing.available === true,
      mail: rows(signing.mail, (row): AutopilotMail | null => {
        const at = time(row.at);
        return at && typeof row.kind === 'string' && MAIL_KINDS.has(row.kind)
          ? { at, kind: row.kind as AutopilotMailKind, to: str(row.to) } : null;
      }),
    } : null;
  }
  return {
    ...more,
    generated_at: time(data.generated_at),
    cold_line: coldLine,
    taylor: taylor ? {
      available: taylor.available === true,
      email: rows(taylor.email, (row) => {
        const at = time(row.at);
        return at ? { at, purpose: str(row.purpose), subject: str(row.subject), status: str(row.status), reason: str(row.reason),
          to_name: str(row.to_name), for: person(row.for), text: typeof row.text === 'string' ? row.text : null } : null;
      }),
      texts: rows(taylor.texts, (row) => {
        const at = time(row.at);
        return at ? { at, who: who(row.who), text: typeof row.text === 'string' ? row.text : null, status: status(row.status),
          name: str(row.name), for: person(row.for) } : null;
      }),
    } : null,
    unknowns: Array.isArray(data.unknowns) ? data.unknowns.filter((u): u is string => typeof u === 'string' && u.trim() !== '') : [],
  };
}

function normalizeEngineToday(data: unknown): EngineTodayResponse {
  if (!isRecord(data)) throw new TypeError('invalid engine today response');
  const normalized = { ...data };
  if (data.ledger !== undefined && data.ledger !== null) {
    normalized.ledger = normalizeNeedLedger(data.ledger);
  }
  const completed = data.quietly_completed;
  if (completed !== null && (!Array.isArray(completed) || !completed.every(validCompletedTarget))) {
    normalized.quietly_completed = null;
  }
  return normalized as unknown as EngineTodayResponse;
}

let onUnauthorized: (() => void) | null = null;
let onLocked: (() => void) | null = null;
let unlockTokenProvider: () => string | null = () => null;

export function setUnauthorizedHandler(handler: () => void) {
  onUnauthorized = handler;
}

export function setLockedHandler(handler: () => void) {
  onLocked = handler;
}

export function setUnlockTokenProvider(provider: () => string | null) {
  unlockTokenProvider = provider;
}

/**
 * Strip everything a bearer token can never legitimately contain.
 *
 * A phone keyboard or clipboard can slip a smart quote, a zero-width space, or some other
 * non-Latin-1 blank into a pasted token. AuthPrompt's .trim() does not remove those, so the
 * junk reaches Headers.set('Authorization', ...), which throws "String contains non
 * ISO-8859-1 code point" instead of failing as a bad credential. The owner then sees a
 * DOM exception that names nothing he can act on and cannot log in at all (2026-08-15,
 * during a phone migration). Tokens here are base64url ASCII, so anything outside
 * printable ASCII (and the space itself) is noise and is dropped.
 */
export function sanitizeToken(raw: string): string {
  // eslint-disable-next-line no-control-regex
  return raw.replace(/[^\x21-\x7E]/g, '');
}

class ApiClient {
  private token: string;

  constructor() {
    // Sanitize on read as well as write: a token poisoned by an older build is already
    // sitting in localStorage and would keep throwing on every request until cleared.
    this.token = sanitizeToken(localStorage.getItem('jarvis_token') || '');
  }

  setToken(token: string) {
    this.token = sanitizeToken(token);
    localStorage.setItem('jarvis_token', this.token);
  }

  getToken(): string {
    return this.token;
  }

  hasToken(): boolean {
    return this.token.length > 0;
  }

  getPreferences(): Promise<AppPreferences> {
    return this.request('/api/preferences');
  }

  updatePreferences(preferences: Pick<AppPreferences, 'textScale'>): Promise<AppPreferences> {
    return this.request('/api/preferences', {
      method: 'PUT',
      body: JSON.stringify(preferences),
    });
  }

  private authHeaders(headers?: HeadersInit, includeJson = false): Headers {
    const result = new Headers(headers);
    result.set('Authorization', `Bearer ${this.token}`);
    if (includeJson && !result.has('Content-Type')) {
      result.set('Content-Type', 'application/json');
    }
    const unlockToken = unlockTokenProvider();
    if (unlockToken) result.set('X-Jarvis-Unlock', unlockToken);
    return result;
  }

  private async throwResponseError(res: Response): Promise<never> {
    const data = await res.json().catch(() => ({ error: res.statusText }));
    // A "locked" response means the PWA lock wants a PIN or fingerprint. It must NEVER be
    // mistaken for a bad bearer token, because onUnauthorized() clears jarvis_token and
    // throws the user out of the whole console. The lock API now answers 423 for every
    // locked state (401 is reserved for bearer failure alone), but an error:'locked' body
    // is treated as locked regardless of status as defence in depth: an adversarial review
    // on 2026-07-26 found that a single stray 401 from the credentials endpoint logged the
    // owner out mid-enrolment, before he could add his first fingerprint.
    const isLocked = res.status === 423 || data?.error === 'locked';
    if (isLocked) onLocked?.();
    else if (res.status === 401) onUnauthorized?.();
    const message = res.status === 401 && !isLocked
      ? 'Unauthorized'
      : data?.error || `HTTP ${res.status}`;
    throw new ApiError(message, res.status, data);
  }

  private async requestOnce<T>(path: string, options: RequestInit = {}): Promise<T> {
    const res = await fetch(path, {
      ...options,
      headers: this.authHeaders(options.headers, true),
    });

    if (!res.ok) await this.throwResponseError(res);

    return res.json();
  }

  private request<T>(path: string, options: RequestInit = {}, bounded = true): Promise<T> {
    return bounded && (options.method || 'GET').toUpperCase() === 'GET'
      ? this.requestWithTimeout<T>(path, options)
      : this.requestOnce<T>(path, options);
  }

  lockRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
    // The public /api/voice/* prefix belongs to the webhook service.
    if (path === '/api/voice/talk') path = '/api/talk';
    if (path === '/api/voice/receipt') path = '/api/talk/receipt';
    return this.request<T>(path, options);
  }

  private async requestWithTimeout<T>(
    path: string,
    options: RequestInit = {},
    timeoutMs = 12_000,
  ): Promise<T> {
    const controller = new AbortController();
    const cancel = () => controller.abort(options.signal?.reason);
    options.signal?.addEventListener('abort', cancel, { once: true });
    if (options.signal?.aborted) cancel();
    const timer = setTimeout(() => controller.abort(
      new DOMException('Request timed out. Please try again.', 'AbortError'),
    ), timeoutMs);
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let onAbort!: () => void;
    const aborted = new Promise<never>((_, reject) => {
      onAbort = () => reject(controller.signal.reason);
      controller.signal.addEventListener('abort', onAbort, { once: true });
      if (controller.signal.aborted) onAbort();
    });
    try {
      if (controller.signal.aborted) return await aborted;
      const run = async (): Promise<T> => {
        try {
          return await this.request<T>(path, { ...options, signal: controller.signal }, false);
        } catch (error) {
          const retryable = error instanceof TypeError || (
            error instanceof ApiError && [502, 503, 504].includes(error.status)
            && !(isRecord(error.data) && error.data.error === 'locked')
          );
          if (controller.signal.aborted || (options.method || 'GET').toUpperCase() !== 'GET'
            || !retryable) throw error;
          await Promise.race([
            new Promise<void>((resolve) => { retryTimer = setTimeout(resolve, 250); }),
            aborted,
          ]);
          if (controller.signal.aborted) throw controller.signal.reason;
          return this.request<T>(path, { ...options, signal: controller.signal }, false);
        }
      };
      // Bound headers, JSON body consumption and retry together, even if a
      // transport does not promptly reject its own promise after cancellation.
      return await Promise.race([run(), aborted]);
    } finally {
      clearTimeout(timer);
      clearTimeout(retryTimer);
      options.signal?.removeEventListener('abort', cancel);
      controller.signal.removeEventListener('abort', onAbort);
    }
  }

  private async requestBlob(path: string, options: RequestInit = {}): Promise<Blob> {
    const res = await fetch(path, {
      ...options,
      headers: this.authHeaders(options.headers),
    });

    if (!res.ok) await this.throwResponseError(res);

    return res.blob();
  }

  // ===== Approvals =====

  getApprovals(status: 'pending' | 'recent' = 'pending'): Promise<ApprovalListResponse> {
    return this.request(`/api/approvals?status=${status}`);
  }

  getApproval(id: string): Promise<ApprovalDetailResponse> {
    return this.request(`/api/approvals/${id}`);
  }

  /**
   * Persist an edited draft body on a pending approval. The queue row, not the
   * browser, is what the send path reads, so an edit has to land here before any
   * approve fires.
   */
  saveApprovalDraft(
    id: string,
    body: string,
    opts?: { keepalive?: boolean; decide?: 'approve'; shown?: ApprovalShownFields },
  ): Promise<{ ok: boolean; body: string; decided?: boolean }> {
    return this.request(`/api/approvals/${id}/draft`, {
      method: 'POST',
      keepalive: opts?.keepalive,
      body: JSON.stringify({ body, decide: opts?.decide, shown: opts?.shown }),
    });
  }

  /**
   * `keepalive` lets a decide dispatched while the page is unloading still reach the
   * server. An optimistic approve tells Joseph the message is gone before the request
   * lands; if he backgrounds the PWA in that gap and the request dies with the page, the
   * row stays `pending` and can then be silently auto-expired by brain.py's stale-approval
   * sweep. keepalive is what stops that.
   *
   * `shown` (B1c) is what the card showed; the server approves only onto exactly those fields.
   */
  decideApproval(
    id: string,
    action: string,
    decline?: { declineReason?: string; declineAudioPath?: string },
    opts?: { keepalive?: boolean; shown?: ApprovalShownFields },
  ): Promise<{ ok: boolean; decline_feedback?: { classification: string } | null }> {
    return this.request(`/api/approvals/${id}/decide`, {
      method: 'POST',
      keepalive: opts?.keepalive,
      body: JSON.stringify({
        action,
        decline_reason: decline?.declineReason,
        decline_audio_path: decline?.declineAudioPath,
        shown: opts?.shown,
      }),
    });
  }

  /** `shown` maps each approved card's id to the fields it showed (B1c). */
  batchDecideApprovals(
    ids: string[],
    action: string,
    shown?: Record<string, ApprovalShownFields>,
  ): Promise<ApprovalBatchResult> {
    return this.request('/api/approvals/batch-decide', {
      method: 'POST',
      body: JSON.stringify({ ids, action, shown }),
    });
  }

  // ===== Threads =====

  async getSmsThread(contactId: number): Promise<ThreadResponse> {
    const data = await this.request<RawThreadResponse>(`/api/threads/sms/${contactId}`);
    return normalizeThreadResponse(data, 'sms');
  }

  async getSmsThreadByPhone(phone: string): Promise<ThreadResponse> {
    const data = await this.request<RawThreadResponse>(`/api/threads/phone/${encodeURIComponent(phone)}`);
    return normalizeThreadResponse(data, 'sms');
  }

  async getEmailThreadByAddress(address: string): Promise<ThreadResponse> {
    const data = await this.request<RawThreadResponse>(`/api/threads/address/${encodeURIComponent(address)}`);
    return normalizeThreadResponse(data, 'email');
  }

  async getEmailThread(threadId: string): Promise<ThreadResponse> {
    const data = await this.request<RawThreadResponse>(`/api/threads/email/${threadId}`);
    return normalizeThreadResponse(data, 'email');
  }

  async getContactThreads(contactId: number): Promise<ThreadResponse> {
    const data = await this.request<RawThreadResponse>(`/api/threads/contact/${contactId}`);
    return normalizeThreadResponse(data);
  }

  // ===== Pulse =====

  getPulse(): Promise<PulseData> {
    return this.request('/api/pulse');
  }

  // ===== Deals =====

  getDeals(opts: { includeClosed?: boolean; closedOnly?: boolean; listings?: boolean; days?: number } = {}): Promise<DealsResponse> {
    const params = new URLSearchParams();
    if (opts.closedOnly) params.set('include', 'closed-only');
    else if (opts.includeClosed) params.set('include', 'closed');
    else if (opts.listings) params.set('include', 'listings');
    if (opts.days) params.set('days', String(opts.days));
    const qs = params.toString();
    return this.request(`/api/deals${qs ? '?' + qs : ''}`);
  }

  getDeal(id: string): Promise<DealDetailResponse> {
    return this.request(`/api/deals/${encodeURIComponent(id)}`);
  }

  getDealTimeline(id: string, limit = 100): Promise<DealTimelineResponse> {
    return this.request(`/api/deals/${encodeURIComponent(id)}/timeline?limit=${limit}`);
  }

  getPipelineStats(): Promise<PipelineStats> {
    return this.request('/api/deals/pipeline-stats');
  }

  viewDealDocument(dealId: string, documentId: string): Promise<Blob> {
    return this.requestBlob(`/api/deals/${encodeURIComponent(dealId)}/documents/${encodeURIComponent(documentId)}/view`);
  }

  getDealDocumentPreview(dealId: string, documentId: string): Promise<DealDocumentPreviewResponse> {
    return this.request(`/api/deals/${encodeURIComponent(dealId)}/documents/${encodeURIComponent(documentId)}/preview`);
  }

  viewDealDocumentPage(dealId: string, documentId: string, page: number): Promise<Blob> {
    return this.requestBlob(`/api/deals/${encodeURIComponent(dealId)}/documents/${encodeURIComponent(documentId)}/pages/${page}.png`);
  }

  downloadDealDocument(dealId: string, documentId: string): Promise<Blob> {
    return this.requestBlob(`/api/deals/${encodeURIComponent(dealId)}/documents/${encodeURIComponent(documentId)}/download`);
  }

  downloadDealDocuments(dealId: string, documentIds: string[]): Promise<Blob> {
    return this.requestBlob(`/api/deals/${encodeURIComponent(dealId)}/documents/download`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ document_ids: documentIds }),
    });
  }

  updateCommission(id: string, data: { commission_rate?: number; commission_amount?: number }): Promise<{ success: boolean; deal: unknown }> {
    return this.request(`/api/deals/${encodeURIComponent(id)}/commission`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  getNextClosing(): Promise<NextClosingResponse> {
    return this.request('/api/next-closing');
  }

  completeMilestone(dealId: string, milestoneId: string): Promise<{ ok: boolean }> {
    return this.request(`/api/deals/${encodeURIComponent(dealId)}/milestones/${encodeURIComponent(milestoneId)}/complete`, { method: 'POST' });
  }

  uncompleteMilestone(dealId: string, milestoneId: string): Promise<{ ok: boolean }> {
    return this.request(`/api/deals/${encodeURIComponent(dealId)}/milestones/${encodeURIComponent(milestoneId)}/uncomplete`, { method: 'POST' });
  }

  /** Answer a Build Desk question from Today (business-loop-core C-01b): one option, free text, or both. */
  answerBuildDesk(id: number, answer: { option?: string; text?: string }): Promise<{ ok: boolean }> {
    return this.request(`/api/engine/build-desk/${encodeURIComponent(String(id))}/answer`, {
      method: 'POST',
      body: JSON.stringify(answer),
    });
  }

  /**
   * Answer a decision from Today (business-loop-core C-26t): the option's number (from 1), free text, or both.
   * `seen` is the row's `version`: the server refuses (409) when the need changed after the card was drawn.
   */
  answerDecision(needId: string, answer: { option?: number; text?: string; seen?: string }): Promise<{ ok: boolean }> {
    return this.request(`/api/engine/decisions/${encodeURIComponent(needId)}/answer`, {
      method: 'POST',
      body: JSON.stringify(answer),
    });
  }

  /** Close a Today "Needs you" obligation loop (swipe-right = Done). Internal
   *  bookkeeping only; sends nothing. Idempotent server-side. */
  markObligationDone(obligationId: string): Promise<{ ok: boolean }> {
    return this.request(`/api/obligations/${encodeURIComponent(obligationId)}/done`, { method: 'POST' });
  }

  addDealNote(dealId: string, note: string): Promise<{ success: boolean }> {
    return this.request(`/api/deals/${encodeURIComponent(dealId)}/notes`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
  }

  // ===== Contacts =====

  // Server responds { contacts: [...] } (server/index.js ~line 2497) -- the
  // prior `{ results }` shape here never matched the live route and had no
  // consumers until the P4 people module's search field. Fixed 2026-07-04.
  searchContacts(query: string): Promise<{ contacts: ContactSearchResult[] }> {
    return this.request(`/api/contacts/search?q=${encodeURIComponent(query)}`);
  }

  // contact_id is a unified_contacts UUID (string). The server keys the
  // profile lookup on uc.id = $1, so a numeric id would never match.
  getContactProfile(id: string): Promise<ContactProfile> {
    return this.request(`/api/contacts/${encodeURIComponent(id)}/profile`);
  }

  // ===== Outreach =====

  getOutreachQueue(
    category = 'all',
    limit = 50,
    search = '',
    sort = 'score'
  ): Promise<OutreachQueueResponse> {
    const params = new URLSearchParams({ category, limit: String(limit), sort });
    if (search) params.set('search', search);
    return this.request(`/api/outreach-queue?${params}`);
  }

  getOutreachDraft(contactId: number): Promise<OutreachDraftResponse> {
    return this.request(`/api/outreach-queue/draft/${encodeURIComponent(contactId)}`);
  }

  recordOutreachAction(
    contactId: number,
    actionType: string,
    notes?: string
  ): Promise<{ ok: boolean; id: number }> {
    return this.request('/api/outreach-queue/action', {
      method: 'POST',
      body: JSON.stringify({ contact_id: contactId, action_type: actionType, notes }),
    });
  }

  undoOutreachAction(actionId: number): Promise<{ ok: boolean }> {
    return this.request(`/api/outreach-queue/action/${actionId}`, { method: 'DELETE' });
  }

  getOutreachStats(): Promise<OutreachStatsResponse> {
    return this.request('/api/outreach-queue/stats');
  }

  snoozeOutreachContact(contactId: number, snoozeUntil: string): Promise<{ ok: boolean }> {
    return this.request('/api/outreach-queue/snooze', {
      method: 'POST',
      body: JSON.stringify({ contact_id: contactId, snooze_until: snoozeUntil }),
    });
  }

  cancelOutreachSnooze(snoozeId: number): Promise<{ ok: boolean }> {
    return this.request(`/api/outreach-queue/snooze/${snoozeId}`, { method: 'DELETE' });
  }

  // ===== Messages (SMS) =====

  getMessageThreads(limit = 50, offset = 0): Promise<MessageThreadsResponse> {
    return this.request(`/api/messages/threads?limit=${limit}&offset=${offset}`);
  }

  sendSms(to: string, body: string): Promise<SendSmsResponse> {
    return this.request('/api/messages/send', {
      method: 'POST',
      body: JSON.stringify({ to, body }),
    });
  }

  getAiDraft(threadContext: string[], contactName?: string): Promise<{ draft: string }> {
    return this.request('/api/messages/ai-draft', {
      method: 'POST',
      body: JSON.stringify({ thread_context: threadContext, contact_name: contactName }),
    });
  }

  pinThread(contactPhoneNorm: string): Promise<{ ok: boolean; is_pinned: boolean }> {
    return this.request('/api/messages/threads/pin', {
      method: 'POST',
      body: JSON.stringify({ contact_phone_norm: contactPhoneNorm }),
    });
  }

  archiveThread(contactPhoneNorm: string, undo = false): Promise<{ ok: boolean; is_archived: boolean }> {
    return this.request('/api/messages/threads/archive', {
      method: 'POST',
      body: JSON.stringify({ contact_phone_norm: contactPhoneNorm, undo }),
    });
  }

  // ===== Chat =====

  searchChats(query: string, group: 'business' | 'coding' | 'fantasy' | 'reflections', offset = 0, signal?: AbortSignal): Promise<ChatSearchResponse> {
    const params = new URLSearchParams({ q: query, group, offset: String(offset), limit: '30' });
    return this.request(`/api/chats/search?${params}`, { signal });
  }

  async getChats(): Promise<{ chats: ChatSession[] }> {
    const data = await this.request<unknown>('/api/chats?metadata=1');
    if (!isRecord(data) || !Array.isArray(data.chats) || !data.chats.every((chat) =>
      isRecord(chat) && typeof (chat.chat_id ?? chat.id) === 'number'
      && Number.isSafeInteger(chat.chat_id ?? chat.id)
    )) throw new ApiError('Could not load chats. Please try again.', 502);
    return {
      chats: (data.chats as Array<Record<string, unknown>>).map((c) => ({
        id: (c.chat_id ?? c.id) as number,
        is_pinned: c.is_pinned === true,
        title_pending: c.title_pending === true,
        title: (c.title as string) || 'Chat',
        created_at: c.created_at as string,
        updated_at: (c.updated_at ?? c.created_at) as string,
        message_count: (c.message_count as number) ?? 0,
        channel: (c.channel as 'pwa' | 'signal') || 'pwa',
        private: (c.private as boolean) ?? false,
        profile: (c.profile as string | null) ?? null,
      })),
    };
  }

  async getChatHistory(chatId: number, beforeId?: string): Promise<ChatHistoryPage> {
    // v1 backend uses /api/chat-history?chat_id=xxx
    const data = await this.request<{ messages: Array<Record<string, unknown>>; has_more?: boolean; next_before_id?: string | null }>(
      `/api/chat-history?chat_id=${chatId}&limit=30${beforeId ? `&before_id=${encodeURIComponent(beforeId)}` : ''}`
    );
    return {
      messages: (data.messages ?? []).map((m) => {
        const sender = parseSender(m.sender);
        return {
          id: typeof m.id === 'string' ? m.id : undefined,
          role: (m.role as 'user' | 'assistant'),
          content: (m.content ?? m.message) as string,
          timestamp: (m.timestamp ?? m.time ?? m.created_at) as string,
          ...(sender ? { sender } : {}),
        };
      }),
      has_more: data.has_more === true,
      next_before_id: data.next_before_id ?? null,
    };
  }

  async getChatBackground(chatId: number): Promise<BackgroundTask[]> {
    return this.request(`/api/chats/${encodeURIComponent(chatId)}/background`);
  }

  async createChat(title?: string, channel?: 'pwa' | 'signal'): Promise<{ chat: ChatSession }> {
    const data = await this.request<Record<string, unknown>>('/api/chats', {
      method: 'POST',
      body: JSON.stringify({ title, channel }),
    });
    // v1 returns flat row; v2 expects { chat: ... }
    const raw = (data.chat ?? data) as Record<string, unknown>;
    return {
      chat: {
        id: (raw.chat_id ?? raw.id) as number,
        title: (raw.title as string) || title || 'New Chat',
        title_pending: raw.title_pending === true,
        created_at: (raw.created_at ?? new Date().toISOString()) as string,
        updated_at: (raw.updated_at ?? raw.created_at ?? new Date().toISOString()) as string,
        message_count: 0,
        channel: (raw.channel as 'pwa' | 'signal') || 'pwa',
      },
    };
  }

  async createProfileTab(profile = 'coding'): Promise<{ chat: ChatSession }> {
    const data = await this.request<Record<string, unknown>>('/api/profile-tab', {
      method: 'POST',
      body: JSON.stringify({ profile }),
    });
    const raw = (data.chat ?? data) as Record<string, unknown>;
    return {
      chat: {
        id: (raw.chat_id ?? raw.id) as number,
        title: (raw.title as string) || '\u{1F6E0} Coding Agent',
        title_pending: raw.title_pending === true,
        created_at: (raw.created_at ?? new Date().toISOString()) as string,
        updated_at: (raw.updated_at ?? raw.created_at ?? new Date().toISOString()) as string,
        message_count: 0,
        channel: (raw.channel as 'pwa' | 'signal') || 'pwa',
        profile: (raw.profile as string | null) ?? profile,
      },
    };
  }

  async createReflectionsTab(): Promise<{ chat: ChatSession }> {
    const data = await this.request<Record<string, unknown>>('/api/reflections-tab', {
      method: 'POST',
      body: JSON.stringify({}),
    });
    const raw = (data.chat ?? data) as Record<string, unknown>;
    return {
      chat: {
        id: (raw.chat_id ?? raw.id) as number,
        title: (raw.title as string) || '\u{1F512} Reflections',
        created_at: (raw.created_at ?? new Date().toISOString()) as string,
        updated_at: (raw.updated_at ?? raw.created_at ?? new Date().toISOString()) as string,
        message_count: 0,
        channel: (raw.channel as 'pwa' | 'signal') || 'pwa',
      },
    };
  }

  deleteChat(chatId: number): Promise<{ ok: boolean }> {
    return this.request(`/api/chats/${chatId}`, { method: 'DELETE' });
  }

  renameChat(chatId: number, title: string): Promise<{ ok: boolean }> {
    return this.request(`/api/chats/${chatId}`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    });
  }

  // ===== ISA (Inside Sales Agent) =====

  getIsaApprovals(status = 'pending'): Promise<ISAApprovalListResponse> {
    return this.request(`/api/isa/approvals?status=${status}`);
  }

  getIsaThreads(): Promise<ISAThreadsResponse> {
    return this.request('/api/isa/threads');
  }

  getIsaThread(phone: string): Promise<ISAThreadDetailResponse> {
    return this.request(`/api/isa/threads/${encodeURIComponent(phone)}`);
  }

  sendIsaMessage(phone: string, body: string): Promise<ISASendResponse> {
    return this.request('/api/isa/send', {
      method: 'POST',
      body: JSON.stringify({ phone, body }),
    });
  }

  pinIsaThread(phone: string): Promise<{ ok: boolean; is_pinned: boolean }> {
    return this.request('/api/isa/threads/pin', {
      method: 'POST',
      body: JSON.stringify({ phone }),
    });
  }

  archiveIsaThread(phone: string, undo = false): Promise<{ ok: boolean; is_archived: boolean }> {
    return this.request('/api/isa/threads/archive', {
      method: 'POST',
      body: JSON.stringify({ phone, undo }),
    });
  }

  createIsaThread(phone: string, name?: string): Promise<{ ok: boolean; thread: import('./types').ISAThread }> {
    return this.request('/api/isa/threads/new', {
      method: 'POST',
      body: JSON.stringify({ phone, name }),
    });
  }

  // ===== Upload =====

  private async uploadChunk(
    uploadId: string,
    offset: number,
    chunk: Uint8Array,
    hash: string,
  ): Promise<void> {
    let lastError = 'Network error';
    for (let attempt = 0; attempt < CHUNK_UPLOAD_ATTEMPTS; attempt += 1) {
      try {
        const res = await fetch(`/pwa-upload/${encodeURIComponent(uploadId)}/chunk?offset=${offset}`, {
          method: 'PUT',
          headers: this.authHeaders({
            'Content-Type': 'application/octet-stream',
            'X-Chunk-SHA256': hash,
          }),
          body: chunk as BodyInit,
        });
        if (res.ok) return;
        await this.throwResponseError(res);
      } catch (error) {
        if (error instanceof ApiError) {
          lastError = error.message;
          if (error.status < 500 || attempt === CHUNK_UPLOAD_ATTEMPTS - 1) throw error;
        } else {
          lastError = error instanceof Error ? error.message : String(error);
        }
        if (attempt === CHUNK_UPLOAD_ATTEMPTS - 1) break;
      }
      await new Promise<void>((resolve) => setTimeout(resolve, 250 * (2 ** attempt)));
    }
    throw new ApiError(`Network error while uploading a file chunk: ${lastError}`, 0);
  }

  private async abortUpload(uploadId: string): Promise<void> {
    try {
      await fetch(`/pwa-upload/${encodeURIComponent(uploadId)}`, {
        method: 'DELETE',
        headers: this.authHeaders(),
      });
    } catch {
      // The server also expires abandoned sessions after 24 hours.
    }
  }

  async uploadFile(file: File): Promise<{ url: string; name: string; type: string }> {
    if (file.size === 0) {
      throw new ApiError(
        'File contained 0 bytes. If picked from a cloud app, save it to your phone first.',
        0,
      );
    }

    let uploadId = '';
    try {
      const session = await this.request<{ upload_id: string; chunk_size: number }>(
        '/pwa-upload/start',
        {
          method: 'POST',
          body: JSON.stringify({ name: file.name, type: file.type, size: file.size }),
        },
      );
      uploadId = session.upload_id;
      if (!uploadId || !Number.isSafeInteger(session.chunk_size) || session.chunk_size <= 0) {
        throw new ApiError('Upload server returned an invalid session', 500);
      }

      for (const { start, end } of tailFirstRanges(file.size, session.chunk_size)) {
        const chunk = await readRangeExactly(file, start, end);
        const hash = await sha256Hex(chunk);
        await this.uploadChunk(uploadId, start, chunk, hash);
      }

      const result = await this.request<{
        url?: string;
        path?: string;
        name?: string;
        filename?: string;
        type?: string;
        mimeType?: string;
        size: number;
        sha256: string;
      }>(`/pwa-upload/${encodeURIComponent(uploadId)}/complete`, { method: 'POST' });
      if (result.size !== file.size || !/^[0-9a-f]{64}$/i.test(result.sha256 || '')) {
        throw new ApiError('Upload server failed the final integrity check', 500);
      }
      return {
        url: result.url || result.path || '',
        name: result.name || result.filename || file.name,
        type: result.type || result.mimeType || file.type,
      };
    } catch (error) {
      if (uploadId) await this.abortUpload(uploadId);
      const message = error instanceof Error ? error.message : String(error);
      throw new ApiError(
        `${message}. No incomplete file was attached. Save or export the recording to My Files, then select it again if the problem continues.`,
        error instanceof ApiError ? error.status : 0,
      );
    }
  }

  // ===== Voice =====

  async speakReply(text: string, signal: AbortSignal): Promise<Blob> {
    const response = await fetch('/api/briefing/speak', {
      method: 'POST',
      headers: { Authorization: `Bearer ${this.getToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
      signal,
    });
    if (!response.ok || !response.headers.get('Content-Type')?.startsWith('audio/')) {
      throw new ApiError('Reply audio unavailable', response.status);
    }
    const audio = await response.blob();
    if (!audio.size) throw new ApiError('Reply audio unavailable', 502);
    return audio;
  }

  async transcribe(audioBlob: Blob): Promise<{ text: string; saved?: string }> {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');

    const res = await fetch('/api/transcribe', {
      method: 'POST',
      headers: this.authHeaders(),
      body: formData,
    });

    if (!res.ok) {
      // Surface the server's real reason, and whether the recording survived.
      // A generic throw here is how a 25-minute dictation vanished with no
      // explanation on 2026-07-20; the server now always reports where it saved.
      let detail = '';
      let saved = '';
      try {
        const body = await res.json();
        detail = typeof body?.error === 'string' ? body.error : '';
        saved = typeof body?.saved === 'string' ? body.saved : '';
      } catch {
        // non-JSON error body (gateway/proxy timeout); fall through to generic text
      }
      if (!detail) {
        detail = res.status === 504
          ? 'Transcription timed out.'
          : `Transcription failed (${res.status}).`;
      }
      if (saved) detail += ` Recording saved: ${saved}`;
      throw new ApiError(detail, res.status);
    }

    return res.json();
  }

  // ===== Voice Edit =====

  async voiceEditDraft(approvalId: string, data: {
    transcript: string;
    current_draft: string;
    thread_context: string[];
    channel: 'sms' | 'email';
  }): Promise<{ edited_draft: string }> {
    return this.request(`/api/approvals/${approvalId}/voice-edit`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ===== Chat Session Control =====

  async resetChat(chatId: number): Promise<{ ok: boolean; new_session_id?: string }> {
    return this.requestWithTimeout('/api/chat-reset', {
      method: 'POST',
      body: JSON.stringify({ chat_id: chatId }),
    });
  }

  async setEffort(chatId: number, level: string): Promise<{ ok: boolean }> {
    return this.requestWithTimeout('/api/chat-effort', {
      method: 'POST',
      body: JSON.stringify({ chat_id: chatId, level }),
    });
  }

  async setModel(chatId: number, model: string): Promise<{ ok: boolean; error?: string }> {
    return this.requestWithTimeout('/api/chat-model', {
      method: 'POST',
      body: JSON.stringify({ chat_id: chatId, model }),
    });
  }

  /** Engine toggle (GLOBAL, not per-chat): writes the gateway .codex_mode flag. */
  async setEngine(engine: string): Promise<{ ok: boolean; engine?: string; error?: string }> {
    return this.requestWithTimeout('/api/engine', {
      method: 'POST',
      body: JSON.stringify({ engine }),
    });
  }

  /** Read the current global engine ('claude' | 'codex'). */
  async getEngine(): Promise<{ engine: string }> {
    return this.request('/api/engine', { method: 'GET' });
  }

  async getSessionInfo(chatId: number): Promise<{
    active: boolean;
    session_id_short: string;
    message_count: number;
    context_size_kb: number;
    session_age_minutes: number;
    channel: string;
    effort_level: string;
    model: string;
  }> {
    return this.request('/api/chat-session-info', {
      method: 'POST',
      body: JSON.stringify({ chat_id: chatId }),
    });
  }

  async getCodexModels(): Promise<{ ok: boolean; models: CodexModel[] }> {
    return this.request('/api/codex/models');
  }

  async getEngineProfile(chatId: number): Promise<EngineProfile> {
    return this.requestWithTimeout(`/api/chat-engine-profile?chat_id=${encodeURIComponent(chatId)}`);
  }

  async setEngineProfile(chatId: number, patch: EngineProfilePatch): Promise<EngineProfile> {
    const response = await this.requestWithTimeout<EngineProfile & { ok?: boolean; error?: string }>(
      '/api/chat-engine-profile', {
        method: 'POST',
        body: JSON.stringify({ chat_id: chatId, ...patch }),
      },
    );
    if (response.ok === false) {
      throw new ApiError(response.error || 'Engine profile update rejected', 400);
    }
    return response;
  }

  async getSessions(): Promise<{ ok: boolean; sessions: TerminalSession[] }> {
    const results = await Promise.allSettled([
      this.requestWithTimeout<{ ok: boolean; sessions: TerminalSession[] }>('/api/sessions'),
      this.requestWithTimeout<{ ok: boolean; sessions: TerminalSession[] }>('/pwa-codex-sessions'),
    ]);
    const available = results.filter((result) => result.status === 'fulfilled');
    if (!available.length) throw new Error('Could not load terminals.');
    const sessions = new Map<string, TerminalSession>();
    for (const result of available) {
      for (const session of result.value.sessions ?? []) sessions.set(session.name, session);
    }
    return { ok: true, sessions: [...sessions.values()] };
  }

  async authorizeTerminal(): Promise<{ ok: boolean; expires_at: number }> {
    return this.request('/pwa-terminal-auth', { method: 'POST' });
  }

  async sendSessionKeys(name: string, keys: string): Promise<{ ok: boolean; error?: string }> {
    return this.request(`/api/sessions/${encodeURIComponent(name)}/send`, {
      method: 'POST',
      body: JSON.stringify({ keys }),
    });
  }

  // ===== Chat Stop =====

  async stopChat(chatId: number): Promise<{ ok: boolean; stopped: number }> {
    return this.request('/api/chat/stop', {
      method: 'POST',
      body: JSON.stringify({ chat_id: chatId }),
    });
  }

  getChatStatus(chatId?: number): Promise<ChatStatusResponse> {
    const query = chatId == null ? '' : `?chat_id=${encodeURIComponent(chatId)}`;
    return this.requestWithTimeout(`/api/chat-status${query}`, {}, 12_000);
  }

  getChatSteering(chatId: number, turnId?: string): Promise<ChatSteeringResponse> {
    const query = new URLSearchParams({ chat_id: String(chatId) });
    if (turnId) query.set('turn_id', turnId);
    return this.requestWithTimeout(`/pwa-chat-steer?${query}`, { cache: 'no-store' });
  }

  addChatContext(chatId: number, turnId: string, id: string, text: string): Promise<ChatSteeringResponse> {
    return this.requestWithTimeout('/pwa-chat-steer', {
      method: 'POST',
      body: JSON.stringify({ chat_id: chatId, turn_id: turnId, id, text }),
    });
  }

  getChatPrompts(chatId: number): Promise<ChatPromptsResponse> {
    const query = new URLSearchParams({ chat_id: String(chatId) });
    return this.requestWithTimeout(`/pwa-chat-prompts?${query}`, { cache: 'no-store' });
  }

  answerChatPrompt(
    chatId: number,
    promptId: string,
    answer: ChatPromptAnswer,
  ): Promise<{ ok: boolean; turn_id?: string }> {
    return this.requestWithTimeout('/pwa-chat-prompt-answer', {
      method: 'POST',
      body: JSON.stringify({ chat_id: chatId, prompt_id: promptId, answer }),
    }, 20_000);
  }

  acknowledgeChatTurn(turnId: string): Promise<{ ok: boolean; turn_id: string }> {
    return this.requestWithTimeout('/api/chat-status/ack', {
      method: 'POST',
      body: JSON.stringify({ turn_id: turnId }),
    }, 12_000);
  }

  // ===== Dashboard =====

  getDashboardNow(includeSnoozed = false): Promise<DashboardNowResponse> {
    const qs = includeSnoozed ? '?include_snoozed=true' : '';
    return this.request(`/api/dashboard/now${qs}`);
  }

  getDashboardPulse(): Promise<unknown> {
    return this.request('/api/dashboard/pulse');
  }

  getDashboardStream(since?: string, limit = 100): Promise<{ events: unknown[]; next_before: string | null }> {
    const params = new URLSearchParams({ limit: String(limit) });
    if (since) params.set('since', since);
    return this.request(`/api/dashboard/stream?${params}`);
  }

  addDashboardItem(body: {
    title: string;
    body?: string;
    urgency?: string;
    tag_text?: string;
    source_url?: string;
  }): Promise<{ item: DashboardItem }> {
    return this.request('/api/dashboard/now', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  dismissDashboardItem(id: string): Promise<{ ok: boolean; id: string }> {
    return this.request(`/api/dashboard/now/${encodeURIComponent(id)}/dismiss`, { method: 'POST' });
  }

  snoozeDashboardItem(id: string, hours: number): Promise<{ ok: boolean; id: string; snooze_until: string }> {
    return this.request(`/api/dashboard/now/${encodeURIComponent(id)}/snooze`, {
      method: 'POST',
      body: JSON.stringify({ hours }),
    });
  }

  pinDashboardItem(id: string, pinned: boolean): Promise<{ ok: boolean; id: string; pinned: boolean }> {
    return this.request(`/api/dashboard/now/${id}/pin`, {
      method: 'POST',
      body: JSON.stringify({ pinned }),
    });
  }

  /** Bulk-dismiss open, unpinned items at `urgency` or below (info < soon < urgent).
   *  The server caps `max` at 50 rows per call, so a full clear drains in rounds --
   *  see `clearAllFeedItems` in modules/today/jarvis-feed-store. */
  cleanupDashboardNow(
    urgency: 'info' | 'soon' | 'urgent',
    max: number
  ): Promise<{ ok: boolean; dismissed: number; urgency: string }> {
    return this.request('/api/dashboard/cleanup-now', {
      method: 'POST',
      body: JSON.stringify({ urgency, max }),
    });
  }

  markDashboardSeen(): Promise<{ ok: boolean }> {
    return this.request('/api/dashboard/seen', { method: 'POST' });
  }

  getDashboardDigest(): Promise<unknown> {
    return this.request('/api/dashboard/digest');
  }

  clearStaleDashboard(days = 5): Promise<{ ok: boolean; dismissed: number; days: number }> {
    return this.request('/api/dashboard/clear-stale', {
      method: 'POST',
      body: JSON.stringify({ days }),
    });
  }

  cleanupNowDashboard(urgency: 'info' | 'soon' | 'urgent' = 'info'): Promise<{ ok: boolean; dismissed: number; urgency: string }> {
    return this.request('/api/dashboard/cleanup-now', {
      method: 'POST',
      body: JSON.stringify({ urgency }),
    });
  }

  ackHero(headline: string, hours = 4): Promise<{ ok: boolean; ack: { until: string; acked_at: string } }> {
    return this.request('/api/dashboard/hero/acknowledge', {
      method: 'POST',
      body: JSON.stringify({ headline, hours }),
    });
  }

  clearHeroAck(): Promise<{ ok: boolean }> {
    return this.request('/api/dashboard/hero/clear-ack', { method: 'POST' });
  }

  // ===== Web Push =====

  getPushPublicKey(): Promise<{ publicKey: string }> {
    return this.request('/api/push/public-key');
  }

  subscribePush(body: {
    endpoint: string;
    keys: { p256dh: string; auth: string };
    userAgent: string;
  }): Promise<{ ok: boolean }> {
    return this.request('/api/push/subscribe', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  unsubscribePush(endpoint: string): Promise<{ ok: boolean }> {
    return this.request('/api/push/unsubscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint }),
    });
  }

  // ===== Awareness (W4.3 PHASE 1) =====

  // Throws ApiError(404) when the endpoint is not yet deployed — callers use
  // that to hide the strip gracefully so the UI ships before the API does.
  getAwareness(): Promise<AwarenessResponse> {
    return this.request('/api/awareness');
  }

  // ===== Today (W4.5) =====

  // Server-aggregated single round trip; sections degrade to null but the
  // endpoint never 500s. Throws ApiError(404) before the endpoint is deployed,
  // which the Today store uses to hide the view gracefully.
  getToday(): Promise<TodayResponse> {
    return this.request('/api/today');
  }

  async getEngineHealth(signal?: AbortSignal): Promise<EngineHealthResponse> {
    const controller = new AbortController();
    const abortFromCaller = () => controller.abort(signal?.reason);
    if (signal?.aborted) abortFromCaller();
    else signal?.addEventListener('abort', abortFromCaller, { once: true });
    const timer = setTimeout(() => controller.abort(), 12_000);
    try {
      return await this.request('/api/engine/health', { signal: controller.signal });
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', abortFromCaller);
    }
  }

  async getAutopilot(signal?: AbortSignal): Promise<AutopilotResponse> {
    return normalizeAutopilot(await this.request<unknown>('/api/engine/autopilot', { signal }));
  }

  async getReaching(signal?: AbortSignal): Promise<ReachingResponse> {
    return this.request('/api/engine/reaching', { signal });
  }

  async getCoreStatus(signal?: AbortSignal): Promise<CoreStatusResponse> {
    return normalizeCoreStatus(await this.request<unknown>('/api/engine/core-status', { signal }));
  }

  async getLedgerToday(signal?: AbortSignal): Promise<LedgerTodayResponse> {
    return normalizeLedgerToday(await this.request<unknown>('/api/engine/ledger-today', { signal }));
  }

  async getPersonTexts(id: string): Promise<PersonTextsResponse> {
    return normalizePersonTexts(await this.request(`/api/engine/person-texts/${encodeURIComponent(id)}`), id);
  }

  async getCorePerson(id: string): Promise<CorePersonResponse> {
    return normalizeCorePerson(await this.request<unknown>(`/api/engine/person/${encodeURIComponent(id)}`));
  }

  async getEngineToday(signal?: AbortSignal): Promise<EngineTodayResponse> {
    const controller = new AbortController();
    const abortFromCaller = () => controller.abort(signal?.reason);
    if (signal?.aborted) abortFromCaller();
    else signal?.addEventListener('abort', abortFromCaller, { once: true });
    const timer = setTimeout(() => controller.abort(), 12_000);
    try {
      const response = normalizeEngineToday(await this.request<unknown>('/api/engine/today', { signal: controller.signal }));
      const ledger = response.ledger;
      if (ledger) {
        const seen = new Set<string>();
        const cursors = new Set<string>();
        const checkRows = (page: typeof ledger) => {
          for (const group of ENGINE_LEDGER_GROUPS) {
            for (const row of page[group]) {
              if (seen.has(row.need_id)) throw new Error('Need census changed; retry');
              seen.add(row.need_id);
            }
          }
        };
        checkRows(ledger);
        while (ledger.next_cursor !== null) {
          const cursor = ledger.next_cursor;
          if (typeof cursor !== 'string' || cursors.has(cursor)) throw new Error('Invalid ledger cursor');
          cursors.add(cursor);
          const page = normalizeEngineToday(await this.request<unknown>(
            `/api/engine/today?cursor=${encodeURIComponent(cursor)}`, { signal: controller.signal },
          )).ledger;
          if (!page || page.source_count !== ledger.source_count || page.excluded_count !== ledger.excluded_count
              || ENGINE_LEDGER_GROUPS.some((group) => page.counts[group] !== ledger.counts[group])
              || page.counts.total !== ledger.counts.total
              || !sameReasonCounts(page.excluded_reasons, ledger.excluded_reasons)) {
            throw new Error('Need census changed; retry');
          }
          checkRows(page);
          for (const group of ENGINE_LEDGER_GROUPS) ledger[group].push(...page[group]);
          ledger.next_cursor = page.next_cursor;
        }
        if (seen.size !== ledger.counts.total
            || ENGINE_LEDGER_GROUPS.some((group) => ledger[group].length !== ledger.counts[group])) {
          throw new Error('Incomplete need census; retry');
        }
      }
      return response;
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', abortFromCaller);
    }
  }

  // ===== Telegram timeline (W4.3p2 step 3, read-only) =====

  // Read-only mirror of Joseph's Telegram conversation with Jarvis. Page size
  // via `limit`; pass `before` (an ISO ts) to page OLDER turns. Like getToday/
  // getAwareness, throws ApiError(404) before the endpoint is deployed, which
  // the Telegram store uses to hide the virtual thread gracefully (ship-before-API).
  getTelegramTimeline(opts: { limit?: number; before?: string } = {}): Promise<TelegramTimelineResponse> {
    const params = new URLSearchParams();
    params.set('limit', String(opts.limit ?? 50));
    if (opts.before) params.set('before', opts.before);
    return this.request(`/api/telegram-timeline?${params}`);
  }

  getOwnerAvailability(): Promise<OwnerAvailability> {
    return this.request('/api/owner-availability');
  }

  editOwnerAvailability(patch: Partial<Omit<OwnerAvailability, 'timezone'>>): Promise<OwnerAvailability & {readback: OwnerAvailability; audit: {changed: string[]}}> {
    return this.request('/api/owner-availability', { method: 'PATCH', body: JSON.stringify(patch) });
  }

  // ===== People (routes_v2.js, live) =====

  getPeople(segment: string): Promise<PeopleResponse> {
    // The public Caddy config sends /api/* to the legacy :3200 backend. This
    // same-origin v2 path reaches the canonical edge handler on :18789.
    return this.request(`/pwa-api/people?segment=${encodeURIComponent(segment)}`);
  }

  getPersonLifecycle(id: string): Promise<PeoplePersonResponse> {
    return this.request(`/pwa-api/people/${encodeURIComponent(id)}`);
  }

  searchPeople(query: string): Promise<PeopleSearchResponse> {
    return this.request(`/pwa-api/people/search?q=${encodeURIComponent(query)}`);
  }

  // Queues an approval via the sanctioned writer. Sends nothing itself.
  draftForPerson(id: string, channel: 'sms', context: string): Promise<PeopleDraftResponse> {
    return this.request(`/api/people/${encodeURIComponent(id)}/draft`, {
      method: 'POST',
      body: JSON.stringify({ channel, context }),
    });
  }

  // ===== Health =====

  health(): Promise<{ status: string; timestamp: string }> {
    return this.request('/api/health');
  }
}

export const api = new ApiClient();

export interface OwnerNotice {
  item_id: string; receipt_id: string; decision_state: 'pending' | 'decided';
  outcome: 'now' | 'next_round' | 'app_only' | null;
  reason: string; reason_source: 'code' | 'judgment' | 'fallback';
  app_state: 'retained'; ping_state: 'not_attempted' | 'pending' | 'provider_accepted' | 'failed' | 'unknown';
  next_round_at: string | null; source: string; subject: string; state: string;
  body: string; app_url: string; created_at: string;
}
export interface OwnerNoticeFeed { items: OwnerNotice[]; next_cursor: string | null }
export function getOwnerNotices({ limit = 50, cursor }: { limit?: number; cursor?: string } = {}): Promise<OwnerNoticeFeed> {
  const query = new URLSearchParams({ limit: String(limit) });
  if (cursor) query.set('cursor', cursor);
  return api.lockRequest(`/api/owner-attention/items?${query}`);
}
export function getOwnerNotice(id: string): Promise<OwnerNotice> {
  return api.lockRequest(`/api/owner-attention/items/${encodeURIComponent(id)}`);
}
